import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { getTranslations } from '../translations';

// RTL languages
const RTL_LANGUAGES = ['he', 'ar', 'fa', 'ur'];

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState(getTranslations('en'));
  const [isRTL, setIsRTL] = useState(false);

  // Load user's language preference on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.user_metadata?.preferred_language) {
          const userLanguage = session.user.user_metadata.preferred_language;
          setCurrentLanguage(userLanguage);
          setTranslations(getTranslations(userLanguage));
          setIsRTL(RTL_LANGUAGES.includes(userLanguage));
        } else {
          // No user or no preferred language: set to English
          setCurrentLanguage('en');
          setTranslations(getTranslations('en'));
          setIsRTL(false);
        }
      } catch (error) {
        // On error, also default to English
        setCurrentLanguage('en');
        setTranslations(getTranslations('en'));
        setIsRTL(false);
        console.error('Error loading user language:', error);
      }
    };

    loadUserLanguage();
  }, []);

  // Update language and save to user metadata
  const changeLanguage = async (languageCode) => {
    try {
      setCurrentLanguage(languageCode);
      setTranslations(getTranslations(languageCode));
      setIsRTL(RTL_LANGUAGES.includes(languageCode));

      // Save to user metadata
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const currentMetadata = session.user.user_metadata || {};
        const updatedMetadata = {
          ...currentMetadata,
          preferred_language: languageCode
        };

        await supabase.auth.updateUser({
          data: updatedMetadata
        });
      }
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  // Translation function with pluralization support
  const t = (key, count = null) => {
    const translation = translations[key];
    if (!translation) return key;
    
    // If no count provided, return the base translation
    if (count === null) return translation;
    
    // Handle pluralization based on language
    if (typeof translation === 'object' && translation.plural) {
      // Use pluralization rules
      return translation.plural(count, currentLanguage);
    }
    
    // Fallback to simple English pluralization
    return count === 1 ? translation : translation + 's';
  };

  const value = {
    currentLanguage,
    changeLanguage,
    translations,
    t,
    isRTL
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 